import React, { useEffect, useRef, useState } from 'react';
import { Button, Loader } from '@vymo/ui/atoms';
import SandboxDialog from './sandboxDialog';
import styles from './index.module.scss';

interface SandboxIframeProps {
  platform: 'ios' | 'android';
  width: number;
  height: number;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
}

function SandboxIframe({
  platform,
  width,
  height,
  showDialog,
  setShowDialog,
}: SandboxIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(false);
  const [appConfig, setAppConfig] = useState<{
    appId: string;
    portalId: string;
  } | null>(null);

  useEffect(() => {
    setAppConfig({ appId: '', portalId: '' });
    setShowDialog(true);
  }, [platform, setShowDialog]);

  useEffect(() => {
    if (!appConfig) return undefined;

    const handleIframeLoad = () => {
      setLoading(false);
    };

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
      return () => {
        iframe.removeEventListener('load', handleIframeLoad);
      };
    }
    return undefined;
  }, [appConfig]);

  if (!appConfig?.appId) {
    return (
      <div className={styles.mobilePlaceholder}>
        <Button onClick={() => setShowDialog(true)}>
          Select the app to load
        </Button>
        {showDialog && (
          <SandboxDialog
            onSelect={(appId, portalId) => {
              setLoading(true);
              setAppConfig({ appId, portalId });
              setShowDialog(false);
            }}
            onClose={() => setShowDialog(false)}
          />
        )}
      </div>
    );
  }

  // Get base URL from environment or fallback to development URL
  const baseUrl = window.location.origin;
  const iframeSrc = `${baseUrl}/${appConfig.appId}/?portalId=${appConfig.portalId}`;

  return (
    <div
      className={styles.iframeContainer}
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      {loading && (
        <div className={styles.loader}>
          <Loader size="medium" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        className={styles.iframe}
        title={`${appConfig.appId} sandbox`}
      />
      {showDialog && (
        <SandboxDialog
          onSelect={(appId, portalId) => {
            setLoading(true);
            setAppConfig({ appId, portalId });
            setShowDialog(false);
          }}
          onClose={() => setShowDialog(false)}
        />
      )}
    </div>
  );
}

export default SandboxIframe;
